const React = require('react');
const T = require('prop-types');
const { useState, useRef, useCallback, useMemo, useEffect } = React;
const { context } = require('app-context');
const useForm = require('hooks/useForm');
const FormConfig = require('./formConfig');
const ResourceTargeting = require('./ResourceTargeting');
const TargetPayload = require('./targetPayload');
const PreventNavigationDialog = require('components/PreventNavigationDialog');
const SimpleDialog = require('components/SimpleDialog');
const { getDisplayImageUrl } = require('utils/image');

const { default: Box } = require('@mui/material/Box');
const { default: Typography } = require('@mui/material/Typography');
const { default: TextField } = require('@mui/material/TextField');
const { default: Button } = require('@mui/material/Button');
const { default: IconButton } = require('@mui/material/IconButton');
const { default: Alert } = require('@mui/material/Alert');
const { default: CircularProgress } = require('@mui/material/CircularProgress');
const { default: Paper } = require('@mui/material/Paper');
const { default: Divider } = require('@mui/material/Divider');
const { useTheme } = require('@mui/material/styles');
const { default: ArrowBackIcon } = require('@mui/icons-material/ArrowBack');
const { default: CloudUploadIcon } = require('@mui/icons-material/CloudUpload');
const { default: LinkIcon } = require('@mui/icons-material/Link');
const { RESOURCE_CREATION_METHODS } = require('utils/constants');
const getApiErrorMessage = require('./getApiErrorMessage');

const api = context.api.nearpeer;
const actions = context.actions;
const redux = context.redux.hooks;

const getStoredImageDisplayUrl = (storedUrl) => {

    if (!storedUrl) {
        return null;
    }

    return getDisplayImageUrl(storedUrl) || storedUrl;
};

const toSavePayload = (rawPayload, { omitEmpty } = {}) => {

    const payload = {};

    Object.entries(rawPayload).forEach(([k, v]) => {

        if (v === undefined) {
            return;
        }

        if (omitEmpty && (v === '' || v === null)) {
            return;
        }

        if (typeof v?.toISOString === 'function') {
            payload[k] = v.toISOString();
            return;
        }

        payload[k] = v === '' ? null : v;
    });

    return payload;
};

module.exports = function AdminResourceUploadPage(props) {

    const { history, match } = props;
    const resourceId = match?.params?.id ? Number(match.params.id) : null;
    const isEdit = !!resourceId;
    const theme = useTheme();
    const defaultCtaBgColor = theme.palette.primary.main;

    const fileInputRef = useRef(null);
    const [defaults, setDefaults] = useState(() => FormConfig.withCtaColorDefaults(
        FormConfig.UPLOAD_EMPTY_DEFAULTS,
        defaultCtaBgColor
    ));
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(isEdit);
    const [error, setError] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [initialImageUrl, setInitialImageUrl] = useState(null);
    const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
    const [urlInput, setUrlInput] = useState('');
    const [urlReplaceMode, setUrlReplaceMode] = useState(false);
    const [targets, setTargets] = useState([]);
    const [initialTargets, setInitialTargets] = useState([]);
    const [targetingValid, setTargetingValid] = useState(isEdit);
    const [targetsDirty, setTargetsDirty] = useState(false);
    const [confirmAllProspectsOpen, setConfirmAllProspectsOpen] = useState(false);
    const [confirmPushNotificationsOpen, setConfirmPushNotificationsOpen] = useState(false);

    const targetsRef = useRef(targets);
    const targetingValidRef = useRef(isEdit);
    const targetsInitializedRef = useRef(false);
    const hasSavedRef = useRef(false);
    const pendingFormDataRef = useRef(null);

    useEffect(() => {

        targetingValidRef.current = targetingValid;
    }, [targetingValid]);

    useEffect(() => {

        targetsRef.current = targets;
    }, [targets]);

    useEffect(() => {

        if (!isEdit) {
            return;
        }

        let skipLoadingComplete = false;

        api.resources.adminList()
            .then(({ entities }) => {

                const resource = entities.resources?.[resourceId];
                if (!resource) {
                    setError('Resource not found');
                    return;
                }

                if (!FormConfig.isImageUploadResource(resource)) {
                    skipLoadingComplete = true;
                    history.replace(FormConfig.getResourceEditPath(resource));
                    return;
                }

                const { targets: resourceTargets, ...resourceFields } = resource;
                const loaded = FormConfig.withCtaColorDefaults(FormConfig.UPLOAD_EMPTY_DEFAULTS, defaultCtaBgColor);
                Object.keys(loaded).forEach((key) => {

                    if (resourceFields[key] != null) {
                        loaded[key] = resourceFields[key];
                    }
                });

                setDefaults(loaded);
                setImageUrl(resource.heroImageUrl || null);
                setInitialImageUrl(resource.heroImageUrl || null);
                setInitialTargets(resourceTargets || []);
            })
            .catch((err) => setError(getApiErrorMessage(err, 'Failed to load resource')))
            .finally(() => {

                if (!skipLoadingComplete) {
                    setLoading(false);
                }
            });
    }, [defaultCtaBgColor, history, isEdit, resourceId]);

    const displayImageUrl = localPreviewUrl || getStoredImageDisplayUrl(imageUrl);

    useEffect(() => () => {

        if (localPreviewUrl) {
            URL.revokeObjectURL(localPreviewUrl);
        }
    }, [localPreviewUrl]);

    const clearLocalPreview = useCallback(() => {

        setLocalPreviewUrl((current) => {

            if (current) {
                URL.revokeObjectURL(current);
            }

            return null;
        });
    }, []);

    const performSave = useCallback(async (formData) => {

        setSaving(true);
        setError(null);

        try {
            const rawPayload = FormConfig.buildImageResourcePayload(imageUrl, formData);
            const payload = toSavePayload(rawPayload, { omitEmpty: !isEdit });
            const payloadWithTargets = TargetPayload.withTargets(payload, targetsRef.current);

            if (isEdit) {
                await api.resources.adminUpdate(resourceId, payloadWithTargets);
            }
            else {
                payloadWithTargets.creationMethod = RESOURCE_CREATION_METHODS.IMAGE_UPLOAD;
                await api.resources.adminCreate(payloadWithTargets);
            }

            redux.dispatch(actions.alerts.notification.push({
                message: isEdit ? 'Changes Saved' : 'Resource Created'
            }));
            hasSavedRef.current = true;
            history.push('/app/admin/resources');
        }
        catch (err) {
            setError(getApiErrorMessage(err, isEdit ? 'Save failed' : 'Failed to create resource'));
        }
        finally {
            setSaving(false);
        }
    }, [history, imageUrl, isEdit, resourceId]);

    const continueSave = useCallback((formData) => {

        if (ResourceTargeting.targetsAllProspects(targetsRef.current)) {
            pendingFormDataRef.current = formData;
            setConfirmAllProspectsOpen(true);
            return;
        }

        return performSave(formData);
    }, [performSave]);

    const handleSave = useCallback((formData) => {

        if (!imageUrl) {
            return;
        }

        if (!targetingValidRef.current) {
            setError('Please complete all targeting rows (choose a school and at least one prospect role), or select "Target all prospects across all schools".');
            return;
        }

        if (FormConfig.shouldConfirmPushNotifications(defaults, formData)) {
            pendingFormDataRef.current = formData;
            setConfirmPushNotificationsOpen(true);
            return;
        }

        return continueSave(formData);
    }, [continueSave, defaults, imageUrl]);

    const handleConfirmPushNotifications = useCallback(() => {

        setConfirmPushNotificationsOpen(false);
        const formData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        if (formData) {
            continueSave(formData);
        }
    }, [continueSave]);

    const handleCancelPushNotifications = useCallback(() => {

        setConfirmPushNotificationsOpen(false);
        pendingFormDataRef.current = null;
    }, []);

    const handleConfirmAllProspects = useCallback(() => {

        setConfirmAllProspectsOpen(false);
        const formData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        if (formData) {
            performSave(formData);
        }
    }, [performSave]);

    const handleCancelAllProspects = useCallback(() => {

        setConfirmAllProspectsOpen(false);
        pendingFormDataRef.current = null;
    }, []);

    const handleTargetsChange = useCallback((newTargets) => {

        setTargets(newTargets);
        if (targetsInitializedRef.current) {
            setTargetsDirty(true);
        }
        else {
            targetsInitializedRef.current = true;
        }
    }, []);

    const resolvedFormConfig = useMemo(() => FormConfig.buildUploadFormConfig({
        defaults,
        onSubmit: handleSave
    }), [defaults, handleSave]);

    const { Form, methods } = useForm(resolvedFormConfig);

    const handleSelectFile = useCallback(async (event) => {

        const file = event.target.files?.[0];

        if (!file) {
            return;
        }

        clearLocalPreview();
        setLocalPreviewUrl(URL.createObjectURL(file));
        setUploading(true);
        setError(null);
        setUrlReplaceMode(false);

        try {
            const { imageUrl: uploadedUrl } = await api.resources.adminUploadImage({ resourceImage: file });
            setImageUrl(uploadedUrl);
            clearLocalPreview();
        }
        catch (err) {
            setError(err.message || 'Failed to upload image');
            clearLocalPreview();
        }
        finally {
            setUploading(false);
            event.target.value = '';
        }
    }, [clearLocalPreview]);

    const handleLoadFromUrl = useCallback(async () => {

        const trimmedUrl = urlInput.trim();

        if (!trimmedUrl) {
            setError('Enter an image URL');
            return;
        }

        setUploading(true);
        setError(null);

        try {
            const { imageUrl: importedUrl } = await api.resources.adminImportImageFromUrl({ imageUrl: trimmedUrl });
            setImageUrl(importedUrl);
            setUrlInput('');
            setUrlReplaceMode(false);
        }
        catch (err) {
            setError(getApiErrorMessage(
                err,
                'Could not load that image URL. Check the link and try again.'
            ));
        }
        finally {
            setUploading(false);
        }
    }, [urlInput]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    const renderCurrentImage = () => {

        if (!displayImageUrl) {
            return null;
        }

        return (
            <Paper variant='outlined' sx={{ p: 1.5, mb: 2 }}>
                <Typography variant='subtitle2' sx={{ fontWeight: 700, mb: 1 }}>
                    Current image
                </Typography>
                <Box
                    component='img'
                    src={displayImageUrl}
                    alt='Current resource promotional image'
                    sx={{
                        width: '100%',
                        maxHeight: 400,
                        objectFit: 'contain',
                        borderRadius: 1,
                        display: 'block',
                        bgcolor: 'grey.100'
                    }}
                />
            </Paper>
        );
    };

    const isFormDirty = !!(localPreviewUrl || imageUrl !== initialImageUrl || methods?.formState?.isDirty || targetsDirty);

    return (
        <Box sx={{ p: 2, maxWidth: 720, mx: 'auto', pb: 6 }}>
            <PreventNavigationDialog
                when={isFormDirty}
                navigate={(path, state) => history.push(path, state)}
                shouldBlockNavigation={() => !hasSavedRef.current && isFormDirty}
                alertMsg='You have unsaved changes. Do you want to save or discard them?'
                saveFunction={() => {
                    const formEl = document.getElementById(FormConfig.UPLOAD_FORM_ID);
                    if (formEl) formEl.requestSubmit();
                }}
                pageHasErrors={true}
            />
            <SimpleDialog
                isOpen={confirmPushNotificationsOpen}
                onRequestClose={handleCancelPushNotifications}
                title='Send push notifications?'
                content='Are you sure you want to send push notifications to all matching users?'
                buttons={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCancelPushNotifications} color='inherit'>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmPushNotifications} color='primary' variant='contained' autoFocus>
                            Yes, send push notifications
                        </Button>
                    </Box>
                }
            />
            <SimpleDialog
                isOpen={confirmAllProspectsOpen}
                onRequestClose={handleCancelAllProspects}
                title='Target all prospects across all schools?'
                content='This resource will be shown to every prospect at every school. Are you sure you want to continue?'
                buttons={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button onClick={handleCancelAllProspects} color='inherit'>
                            Cancel
                        </Button>
                        <Button onClick={handleConfirmAllProspects} color='primary' variant='contained' autoFocus>
                            Yes, target everyone
                        </Button>
                    </Box>
                }
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton
                    onClick={() => history.push(isEdit ? '/app/admin/resources' : '/app/admin/resources/create')}
                    aria-label='Back'
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant='h5' sx={{ fontWeight: 700, ml: 1 }}>
                    {isEdit ? 'Edit Resource' : 'Upload Image'}
                </Typography>
            </Box>

            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                {isEdit
                    ? 'Replace the promotional image or update the CTA, schedule, and targeting rules below.'
                    : 'Upload a resource image or paste an image URL, then configure the CTA, schedule, and targeting rules below.'}
            </Typography>

            {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}

            <input
                ref={fileInputRef}
                type='file'
                accept='image/*'
                hidden
                onChange={handleSelectFile}
            />

            {!imageUrl && (
                <>
                    <Paper
                        variant='outlined'
                        onClick={() => !uploading && fileInputRef.current?.click()}
                        sx={{
                            p: 4,
                            mb: 2,
                            textAlign: 'center',
                            cursor: uploading ? 'default' : 'pointer',
                            borderStyle: 'dashed',
                            bgcolor: 'action.hover'
                        }}
                    >
                        {uploading ? (
                            <CircularProgress size={32} />
                        ) : (
                            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        )}
                        <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                            {uploading ? 'Loading image…' : 'Click to upload an image'}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                            PNG, JPG, WEBP, or GIF up to 10MB
                        </Typography>
                    </Paper>

                    <Divider sx={{ mb: 2 }}>or</Divider>

                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <TextField
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                            placeholder='https://example.com/resource.png'
                            label='Image URL'
                            fullWidth
                            size='small'
                            disabled={uploading}
                            onKeyDown={(e) => {

                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleLoadFromUrl();
                                }
                            }}
                        />
                        <Button
                            variant='outlined'
                            startIcon={uploading ? <CircularProgress size={16} /> : <LinkIcon />}
                            onClick={handleLoadFromUrl}
                            disabled={uploading || !urlInput.trim()}
                            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                        >
                            Load URL
                        </Button>
                    </Box>
                </>
            )}

            {imageUrl && (
                <>
                    {renderCurrentImage()}

                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Button
                            size='small'
                            variant='outlined'
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                        >
                            {uploading ? 'Loading…' : 'Replace with upload'}
                        </Button>
                        <Button
                            size='small'
                            variant={urlReplaceMode ? 'contained' : 'outlined'}
                            onClick={() => setUrlReplaceMode((current) => !current)}
                            disabled={uploading}
                        >
                            {urlReplaceMode ? 'Cancel URL replace' : 'Use URL instead'}
                        </Button>
                    </Box>

                    {urlReplaceMode && (
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                            <TextField
                                value={urlInput}
                                onChange={(e) => setUrlInput(e.target.value)}
                                placeholder='https://example.com/resource.png'
                                label='New image URL'
                                fullWidth
                                size='small'
                                disabled={uploading}
                                onKeyDown={(e) => {

                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleLoadFromUrl();
                                    }
                                }}
                            />
                            <Button
                                variant='outlined'
                                startIcon={uploading ? <CircularProgress size={16} /> : <LinkIcon />}
                                onClick={handleLoadFromUrl}
                                disabled={uploading || !urlInput.trim()}
                                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                                Load URL
                            </Button>
                        </Box>
                    )}

                    {Form}

                    <Divider sx={{ my: 2 }} />

                    <ResourceTargeting initialTargets={initialTargets} onChange={handleTargetsChange} onValidityChange={setTargetingValid} />

                    <Button
                        variant='contained'
                        fullWidth
                        size='large'
                        type='submit'
                        form={FormConfig.UPLOAD_FORM_ID}
                        disabled={saving || uploading || !targetingValid}
                        sx={{ mt: 2, borderRadius: 3 }}
                    >
                        {saving ? <CircularProgress size={22} /> : (isEdit ? 'Save Changes' : 'Create Resource')}
                    </Button>
                </>
            )}
        </Box>
    );
};

module.exports.propTypes = {
    history: T.object,
    match: T.object
};
